import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';

import SendForCommitmentCashflowModal from './SendForCommitmentCashflowModal';
import { combinableBuckets, combineGroupState, defaultEdit, matchesSearch, rowState } from './sendForCommitmentHelpers';
import SendForCommitmentResults from './SendForCommitmentResults';
import SendForCommitmentRow from './SendForCommitmentRow';
import SendForCommitmentSubmitModal from './SendForCommitmentSubmitModal';

import { formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import Button from 'CommonComponents/Button';
import CheckboxInput from 'CommonComponents/CheckboxInput';
import KeyStatTile from 'CommonComponents/KeyStatTile';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import TableSearchInput from 'CommonComponents/TableSearchInput';
import TextButton from 'CommonComponents/TextButton';

import useModal from 'Hooks/useModal';

const FILTER_ALL = 'all';
const FILTER_READY = 'ready';
const FILTER_ATTENTION = 'attention';
const FILTER_SUBMITTED = 'submitted';
const FILTER_REJECTED = 'rejected';

const COLUMN_COUNT = 14;

/**
 * The queue's headline figures, read off the WHOLE queue rather than the filtered
 * view: they are what the reconciler came to the tab to find out, and a set of
 * totals that moved every time a tab was pressed would answer a different
 * question each time.
 */
function QueueSummary({ total, totalValue, readyCount, attentionCount }) {
    return (
        <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4 tw-mb-4">
            <KeyStatTile title={$t('Unmatched actuals')} value={total} size="secondary" />
            <KeyStatTile title={$t('Total value')} value={formatToFixedMoneyWithLocale(totalValue)} size="secondary" />
            <KeyStatTile title={$t('Ready to submit')} value={readyCount} size="secondary" signed signValue={readyCount} />
            <KeyStatTile title={$t('Needs attention')} value={attentionCount} size="secondary" signed signValue={-attentionCount} />
        </div>
    );
}

function QueueFilter({ active, counts, onChange }) {
    const filters = [
        { id: FILTER_ALL, label: $t('All'), count: counts.all },
        { id: FILTER_READY, label: $t('Ready'), count: counts.ready },
        { id: FILTER_ATTENTION, label: $t('Needs attention'), count: counts.attention },
        { id: FILTER_SUBMITTED, label: $t('Submitted'), count: counts.submitted },
        { id: FILTER_REJECTED, label: $t('Rejected'), count: counts.rejected },
    ];

    return (
        <div className="in-nav horizontal" role="tablist">
            {filters.map((filter) => {
                const isActive = active === filter.id;

                return (
                    <button
                        key={filter.id}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        className={classNames('in-nav__item', isActive ? 'active' : 'inactive')}
                        onClick={() => onChange(filter.id)}
                    >
                        {filter.label}
                        <span
                            className={classNames('tw-ms-1.5 tw-rounded-[10px] tw-px-[7px] tw-py-px tw-text-[11px] tw-font-semibold', {
                                'tw-bg-white/25 tw-text-white': isActive,
                                'tw-bg-blue-25 tw-text-blue': !isActive,
                            })}
                        >
                            {filter.count}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

/**
 * The queue's footer, reporting what is selected and offering the submission.
 *
 * Two things keep it off the rows it is reporting on:
 *
 *  - it sits OUTSIDE the table's scroll container, so scrolling a long queue
 *    moves rows underneath a bar that never moves;
 *  - its own wrapper starts where the table ends, and a sticky element cannot
 *    leave its containing block. Sticky alone is not enough: the bar's natural
 *    slot is below a table that is taller than the space left in the viewport,
 *    so `bottom: 0` pins it half way UP the table and parks it on the rows.
 */
function QueueActionBar({ selectedCount, selectedTotal, invalidCount, groups, combinableCount, submitting, onCombineMatching, onUngroupAll, onSubmit }) {
    const blockedBySplit = invalidCount > 0;
    const overBudgetGroups = groups.filter((group) => group.exceeds).length;
    const combinedRows = groups.reduce((sum, group) => sum + group.size, 0);

    // Ordered by what the reconciler most needs to see, so the bar never explains
    // something they cannot act on yet. An over-budget group is reported last
    // because it is the only one that does not stop the submission.
    let hint = $t('Supplier, contract record, reference and cashflow are taken from each actual.');

    if (combinableCount > 0) {
        hint = $t('{{count}} selected row(s) share a supplier and budget line and can be combined into one commitment', { count: combinableCount });
    }

    if (groups.length > 0) {
        hint = $t('{{count}} row(s) combined into {{groups}} commitment(s)', { count: combinedRows, groups: groups.length });
    }

    if (blockedBySplit) {
        hint = $t('{{count}} selected row(s) commit less than their actual value', { count: invalidCount });
    }

    // Reported, never blocking: the excess goes through the budget change process
    // on submission, so this tells the reconciler what they are about to set off
    // rather than standing in its way.
    if (overBudgetGroups > 0) {
        hint = $t('{{count}} combined commitment(s) are worth more than their budget line has left, and will go through the budget change process', {
            count: overBudgetGroups,
        });
    }

    return (
        <div className="tw-mt-3 tw-pb-2">
            <div className="tw-sticky tw-bottom-2 tw-z-10 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-rounded-lg tw-bg-charcoal tw-px-5 tw-py-3 tw-text-white tw-shadow-primary">
                <div>
                    <div className="tw-text-sm tw-font-medium">
                        {$t('{{count}} selected', { count: selectedCount })} &middot;{' '}
                        <span className="tw-font-semibold tw-text-green">{formatToFixedMoneyWithLocale(selectedTotal)}</span> {$t('to be committed')}
                    </div>
                    <div className="tw-mt-0.5 tw-text-xs tw-text-charcoal-25">{hint}</div>
                </div>
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                    {combinableCount > 0 && (
                        <Button variant="secondary" onClick={onCombineMatching}>
                            {$t('Combine matching')}
                        </Button>
                    )}
                    {/* Inherits the bar's white from `.txt-button`, which sets no colour of
                        its own — an undo beside the action it undoes, not a button competing
                        with it. */}
                    {groups.length > 0 && <TextButton onClick={onUngroupAll}>{$t('Ungroup all')}</TextButton>}
                    <Button variant="success" disabled={selectedCount === 0 || blockedBySplit || submitting} loading={submitting} onClick={onSubmit}>
                        {$t('Submit for commitment')}
                    </Button>
                </div>
            </div>
        </div>
    );
}

/**
 * The work queue of flagged allocations, as editable commitment lines.
 *
 * Allocation-shaped, not transaction-shaped: the flag lives on the allocation, so
 * a split transaction appears once per flagged allocation, each with its own
 * budget line and its own share of the amount.
 *
 * Every commitment raised from here is a Direct Commitment; a row whose budget
 * line carries no workflow cannot be procured at all and is listed, explained and
 * locked rather than hidden.
 */
export default function SendForCommitmentQueue({ items, loading, listUsersUrl, currentUserId, submitUrl, onSubmitted }) {
    const [filter, setFilter] = useState(FILTER_ALL);
    const [search, setSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [edits, setEdits] = useState({});
    // allocation id → group id. Only ever what the reconciler combined by hand: a
    // group that falls below two selected rows is ignored rather than pruned, so
    // deselecting a row cannot leave an assignment that has to be chased down.
    const [combineAssignments, setCombineAssignments] = useState({});
    const nextGroupNumber = useRef(1);
    const [users, setUsers] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [results, setResults] = useState(null);
    // Supplier names are read off the rows at submit time: a committed row leaves
    // the listing on the refresh below, so the results panel cannot look it up
    // afterwards.
    const [resultSuppliers, setResultSuppliers] = useState({});

    const confirmModal = useModal();
    const cashflowModal = useModal();
    const [cashflowRowId, setCashflowRowId] = useState(null);

    // One request for the whole table. Every row hosts an owner selector, so
    // letting each fetch its own list would be one request per flagged allocation.
    useEffect(() => {
        if (!listUsersUrl) {
            return undefined;
        }

        let mounted = true;

        axios
            .get(listUsersUrl)
            .then(({ data }) => {
                if (mounted) {
                    setUsers(data?.data || []);
                }
            })
            .catch(() => {
                // A reconciler whose role cannot list users still gets the queue,
                // with an empty owner selector rather than a dead table.
                console.warn('SendForCommitmentQueue: could not load the owner list');
            });

        return () => {
            mounted = false;
        };
    }, [listUsersUrl]);

    // The reconciler owns what they raise — but only where the event actually offers
    // them as an owner. `admin.users.index` lists the event's users, and whoever is
    // reading the queue need not be one of them (a super-admin never is). Defaulting
    // to an id the selector cannot show would leave every row holding an owner the
    // screen says is unset, and submit it.
    const defaultOwnerId = useMemo(() => (users.some((user) => user.id === currentUserId) ? currentUserId : null), [users, currentUserId]);

    const editFor = useCallback((item) => edits[item.id] ?? defaultEdit(item, defaultOwnerId), [edits, defaultOwnerId]);

    const onEditChange = useCallback(
        (id, field, value) => {
            setEdits((prev) => {
                const item = items.find((candidate) => candidate.id === id);
                const current = prev[id] ?? defaultEdit(item, defaultOwnerId);

                return { ...prev, [id]: { ...current, [field]: value } };
            });
        },
        [items, defaultOwnerId],
    );

    const openCashflow = useCallback(
        (id) => {
            setCashflowRowId(id);
            cashflowModal.show();
        },
        [cashflowModal],
    );

    const applyCashflow = useCallback((id, entries) => onEditChange(id, 'cashflow', entries), [onEditChange]);

    // Derived during render rather than stored: every count, every filter and the
    // action bar's totals read the same verdict, so there is nothing to keep in sync.
    const rows = useMemo(() => items.map((item) => ({ item, edit: editFor(item), state: rowState(item, editFor(item)) })), [items, editFor]);

    const counts = useMemo(
        () => ({
            all: rows.length,
            ready: rows.filter((row) => row.state.ready).length,
            // A row whose commitment is already raised is waiting on an approver,
            // not on the reconciler. Counting it here as well as under Submitted
            // would put the same row under two tabs that contradict each other.
            attention: rows.filter((row) => !row.state.ready && !row.state.submitted).length,
            submitted: rows.filter((row) => row.state.submitted).length,
            // Rejected and cancelled both come back to be raised again, and both
            // report through the same badge, so they belong on the same tab.
            rejected: rows.filter((row) => row.state.returned).length,
        }),
        [rows],
    );

    const visibleRows = useMemo(
        () =>
            rows.filter(({ item, state }) => {
                if (!matchesSearch(item, search)) {
                    return false;
                }

                if (filter === FILTER_READY) {
                    return state.ready;
                }

                if (filter === FILTER_ATTENTION) {
                    return !state.ready && !state.submitted;
                }

                if (filter === FILTER_SUBMITTED) {
                    return state.submitted;
                }

                if (filter === FILTER_REJECTED) {
                    return state.returned;
                }

                return true;
            }),
        [rows, filter, search],
    );

    // Select-all acts on what is on screen and can actually be committed. A blocked
    // row is never swept in, which is the whole reason the header box is separate
    // from a plain "toggle everything".
    const selectableVisibleIds = useMemo(() => visibleRows.filter(({ state }) => !state.blocked).map(({ item }) => item.id), [visibleRows]);

    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    const selectedVisibleCount = selectableVisibleIds.filter((id) => selectedSet.has(id)).length;
    const allVisibleSelected = selectableVisibleIds.length > 0 && selectedVisibleCount === selectableVisibleIds.length;
    const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

    const toggleSelectAll = useCallback(() => {
        setSelectedIds((prev) => {
            const next = new Set(prev);

            if (allVisibleSelected) {
                selectableVisibleIds.forEach((id) => next.delete(id));
            } else {
                selectableVisibleIds.forEach((id) => next.add(id));
            }

            return [...next];
        });
    }, [allVisibleSelected, selectableVisibleIds]);

    const toggleSelect = useCallback((id) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((selected) => selected !== id) : [...prev, id]));
    }, []);

    // Selection is held by id, so a refreshed listing can leave ids behind that no
    // longer have a row, and a row can turn blocked between renders. Reading the
    // footer off the CURRENT rows rather than off `selectedIds` keeps the count and
    // the total honest either way.
    //
    // A row committing less than its actual stays selected — the reconciler is
    // mid-edit — but the bar reports it and refuses to submit. Committing MORE is
    // allowed, so only the shortfall counts here.
    const selectedRows = rows.filter(({ item, state }) => !state.blocked && selectedSet.has(item.id));
    const selectedTotal = selectedRows.reduce((sum, { state }) => sum + state.committed, 0);
    const invalidSelectedCount = selectedRows.filter(({ state }) => state.belowActual).length;

    // Rows about to raise their budget line. Not a bar to submitting — the excess goes
    // through the budget change process — but the reconciler is told before it happens.
    const overBudgetSelected = selectedRows.filter(({ state }) => state.aboveUncommitted);

    // Both read off the CURRENT selection rather than off stored state, for the
    // reason the footer totals above are: a row can be refreshed away, deselected
    // or turned blocked between renders, and a group is only a group while its rows
    // are still going to be submitted together.
    const { groups: combineGroups, byRow: combineGroupByRow } = combineGroupState(selectedRows, combineAssignments);
    const combineOffers = combinableBuckets(selectedRows, combineGroupByRow);
    const combinableIds = new Set(combineOffers.flat());

    const combineIds = useCallback((ids) => {
        if (ids.length < 2) {
            return;
        }

        // Numbered outside the updater: React may invoke an updater twice, and a
        // group whose id changed halfway through would come apart.
        const groupId = `combine-${nextGroupNumber.current}`;
        nextGroupNumber.current += 1;

        setCombineAssignments((prev) => {
            const next = { ...prev };

            ids.forEach((id) => {
                next[id] = groupId;
            });

            return next;
        });
    }, []);

    const combineMatching = useCallback(() => {
        // One group per bucket, never one group for everything on offer: two
        // suppliers combined into a single commitment is the payload the server
        // refuses with a 422 over the whole request.
        combineOffers.forEach((ids) => combineIds(ids));
    }, [combineOffers, combineIds]);

    const combineRow = useCallback(
        (id) => {
            const bucket = combineOffers.find((ids) => ids.includes(id));

            if (bucket) {
                combineIds(bucket);
            }
        },
        [combineOffers, combineIds],
    );

    const ungroup = useCallback((groupId) => {
        setCombineAssignments((prev) => Object.fromEntries(Object.entries(prev).filter(([, assigned]) => assigned !== groupId)));
    }, []);

    const ungroupAll = useCallback(() => setCombineAssignments({}), []);

    const totalValue = items.reduce((sum, item) => sum + Number(item.amount ?? 0), 0);

    const submit = useCallback(() => {
        if (!submitUrl || selectedRows.length === 0) {
            return;
        }

        setSubmitting(true);

        // Captured before the request, not after: the refresh that follows drops
        // every committed row out of the listing.
        const names = Object.fromEntries(selectedRows.map(({ item }) => [item.id, item.supplier]));

        const payload = {
            rows: selectedRows.map(({ item, edit, state }) => ({
                allocation_id: item.id,
                price: state.price,
                quantity: state.quantity,
                signed: edit.signed,
                owner_id: edit.ownerId,
                // Omitted entirely for an untouched row, so the server keeps its own
                // single-entry default rather than being handed a copy of it. `locked` is
                // stripped here: it is how the pop-up knows not to let the actual's own
                // entry be retyped, and means nothing to the schedule being submitted.
                ...(state.cashflowAdjusted ? { cashflow: state.cashflow.map(({ date, amount }) => ({ date, amount })) } : {}),
                // Likewise omitted for a row committing on its own, which is most of
                // them: the server reads its absence as exactly that.
                ...(combineGroupByRow[item.id] ? { combine_group: combineGroupByRow[item.id].id } : {}),
            })),
        };

        axios
            .post(submitUrl, payload)
            .then(({ data }) => {
                setResultSuppliers(names);
                setResults(data?.results ?? []);
                confirmModal.hide();

                // Clear only what the server accepted. A failed row stays in the
                // queue and stays selected, so a retry does not ask the reconciler
                // to find it again.
                const cleared = new Set((data?.results ?? []).filter((row) => row.status !== 'failed').map((row) => row.allocation_id));
                setSelectedIds((prev) => prev.filter((id) => !cleared.has(id)));
                // A refused group keeps its grouping so a retry does not ask the
                // reconciler to combine it again; a committed one has nothing left
                // to group.
                setCombineAssignments((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => !cleared.has(Number(id)))));

                if (typeof onSubmitted === 'function') {
                    onSubmitted();
                }
            })
            .catch(() => {
                // Nothing was committed, so the selection is left exactly as it was.
                setResults([{ allocation_id: null, status: 'failed', reason: $t('The submission could not be sent. Nothing was committed.') }]);
                setResultSuppliers({});
                confirmModal.hide();
            })
            .finally(() => {
                setSubmitting(false);
            });
    }, [submitUrl, selectedRows, combineGroupByRow, confirmModal, onSubmitted]);

    return (
        <div>
            {!!results && <SendForCommitmentResults results={results} suppliers={resultSuppliers} onDismiss={() => setResults(null)} />}

            <QueueSummary total={items.length} totalValue={totalValue} readyCount={counts.ready} attentionCount={counts.attention} />

            <div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                <QueueFilter active={filter} counts={counts} onChange={setFilter} />
                <TableSearchInput searchValue={search} placeholder={$t('Search supplier or budget line')} handleChange={(e) => setSearch(e.target.value)} />
            </div>

            <div className="inbud-table_wrapper tw-overflow-auto max-h-50vh tw-relative">
                <table className="reconciliation-table inbud-table" style={{ width: 'max-content' }}>
                    <thead className="tw-sticky tw-top-0" style={{ zIndex: 15 }}>
                        <tr>
                            <th className="tw-w-10">
                                <CheckboxInput
                                    checked={allVisibleSelected}
                                    indeterminate={someVisibleSelected}
                                    disabled={selectableVisibleIds.length === 0}
                                    onChange={toggleSelectAll}
                                    aria-label={$t('Select all')}
                                />
                            </th>
                            <th className="tw-w-72">{$t('Supplier / Description')}</th>
                            <th className="tw-w-28">{$t('Actual date')}</th>
                            <th className="tw-w-64">{$t('Budget line')}</th>
                            <th className="tw-w-32">{$t('Actual value')}</th>
                            <th className="tw-w-52">{$t('Commit as P × Q')}</th>
                            <th className="tw-w-32">{$t('Commit value')}</th>
                            <th className="tw-w-28">{$t('Cashflow')}</th>
                            <th className="tw-w-36">{$t('Uncommitted after')}</th>
                            <th className="tw-w-20">{$t('Workflow')}</th>
                            <th className="tw-w-20">{$t('Signed')}</th>
                            <th className="tw-w-40">{$t('Owner')}</th>
                            <th className="tw-w-56">{$t('Status')}</th>
                            <th className="tw-w-28">{$t('Combine')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={COLUMN_COUNT}>
                                    <LoadingSpinner />
                                </td>
                            </tr>
                        )}
                        {!loading && visibleRows.length === 0 && (
                            <tr>
                                <td colSpan={COLUMN_COUNT} className="tw-py-6 tw-text-center tw-text-charcoal-50">
                                    {$t('No items match the current filter')}
                                </td>
                            </tr>
                        )}
                        {!loading &&
                            visibleRows.map(({ item, edit }) => (
                                <SendForCommitmentRow
                                    key={item.id}
                                    item={item}
                                    edit={edit}
                                    users={users}
                                    selected={selectedSet.has(item.id)}
                                    group={combineGroupByRow[item.id] ?? null}
                                    combinable={combinableIds.has(item.id)}
                                    onToggleSelect={toggleSelect}
                                    onEditChange={onEditChange}
                                    onOpenCashflow={openCashflow}
                                    onCombine={combineRow}
                                    onUngroup={ungroup}
                                />
                            ))}
                    </tbody>
                </table>
            </div>

            <QueueActionBar
                selectedCount={selectedRows.length}
                selectedTotal={selectedTotal}
                invalidCount={invalidSelectedCount}
                groups={combineGroups}
                combinableCount={combinableIds.size}
                submitting={submitting}
                onCombineMatching={combineMatching}
                onUngroupAll={ungroupAll}
                onSubmit={() => confirmModal.show()}
            />

            <SendForCommitmentCashflowModal modal={cashflowModal} row={rows.find(({ item }) => item.id === cashflowRowId) ?? null} onApply={applyCashflow} />

            <SendForCommitmentSubmitModal
                modal={confirmModal}
                count={selectedRows.length}
                // The rows and the commitments they raise are only the same number
                // while nothing is combined.
                commitmentCount={selectedRows.length - combineGroups.reduce((sum, group) => sum + group.size - 1, 0)}
                total={selectedTotal}
                // What is about to change the budget, and by how much. Summed from the
                // shortfall on each row rather than from the commit values, so the figure
                // is the increase the budget lines will take, not what is being committed.
                overBudgetCount={overBudgetSelected.length}
                overBudgetTotal={overBudgetSelected.reduce((sum, { state }) => sum + Math.abs(state.uncommittedAfter ?? 0), 0)}
                submitting={submitting}
                onConfirm={submit}
            />
        </div>
    );
}
